import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../../utils/api';

/**
 * Custom hook for managing deployments data
 * Handles fetching deployments, releases, families for a project
 * @param {string} activeProjectId - The current active project ID
 * @returns {Object} deployments data, families data, releases data, loading states, and fetch functions
 */
export function useDeploymentsData(activeProjectId) {
    // Data states
    const [deployments, setDeployments] = useState([]);
    const [linkedFamilies, setLinkedFamilies] = useState([]);
    const [allFamilies, setAllFamilies] = useState([]);
    const [releases, setReleases] = useState([]);
    const [guiSchemas, setGuiSchemas] = useState([]);
    
    // Loading states
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Fetch deployments list
    const fetchDeploymentsList = useCallback(async () => {
        if (!activeProjectId) {
            setDeployments([]);
            return;
        }
        try {
            const res = await apiFetch(`/api/deployments?projectId=${activeProjectId}`);
            if (res.ok) {
                const data = await res.json();
                setDeployments(data);
            }
        } catch (err) {
            console.error('Failed to fetch deployments list', err);
            setError('Failed to fetch deployments list');
        }
    }, [activeProjectId]);

    // Fetch families data for the project
    const fetchFamiliesData = useCallback(async () => {
        if (!activeProjectId) {
            setLinkedFamilies([]);
            setAllFamilies([]);
            return;
        }

        try {
            // 1. Fetch project details to know linked family IDs
            const projRes = await apiFetch(`/api/projects/${activeProjectId}`);
            let linkedIds = [];
            if (projRes.ok) {
                const data = await projRes.json();
                linkedIds = data.linkedFamilies || data.LinkedFamilies || [];
            }

            // 2. Fetch all product families to match names
            const url = `/api/products?projectId=${activeProjectId}`;
            const famRes = await apiFetch(url);
            let familiesList = [];
            if (famRes.ok) {
                familiesList = await famRes.json();
                setAllFamilies(familiesList);
            }

            // Filter to only show linked families for selection
            const filteredLinked = familiesList.filter(f => 
                linkedIds.includes(f.id) || (f.parentId && linkedIds.includes(f.parentId))
            );
            setLinkedFamilies(filteredLinked);

            return filteredLinked;
        } catch (err) {
            console.error('Failed to fetch families data', err);
            setError('Failed to fetch families data');
            return [];
        }
    }, [activeProjectId]);

    // Fetch releases data
    const fetchReleasesData = useCallback(async () => {
        try {
            const relRes = await apiFetch(`/api/versions`);
            if (relRes.ok) {
                const versionsList = await relRes.json();
                const releasesList = versionsList.filter(b => b.type === 2 || b.type === 'Release');
                setReleases(releasesList);
                return releasesList;
            }
            return [];
        } catch (err) {
            console.error('Failed to fetch releases', err);
            setError('Failed to fetch releases');
            return [];
        }
    }, []);

    const fetchGuiSchemas = useCallback(async () => {
        if (!activeProjectId) {
            setGuiSchemas([]);
            return [];
        }

        try {
            const schemaRes = await apiFetch(`/api/projects/${activeProjectId}/gui-schemas`);
            if (schemaRes.ok) {
                const schemasList = await schemaRes.json();
                const normalized = (Array.isArray(schemasList) ? schemasList : [])
                    .map(schema => ({
                        versionTag: schema.versionTag || schema.VersionTag,
                        isPublished: Boolean(schema.isPublished ?? schema.IsPublished),
                        createdAt: schema.createdAt || schema.CreatedAt
                    }))
                    .filter(schema => schema.versionTag);
                setGuiSchemas(normalized);
                return normalized;
            }
            setGuiSchemas([]);
            return [];
        } catch (err) {
            console.error('Failed to fetch GUI schemas', err);
            setError('Failed to fetch layout versions');
            setGuiSchemas([]);
            return [];
        }
    }, [activeProjectId]);

    // Main data loading function
    const loadPageData = useCallback(async () => {
        if (!activeProjectId) {
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        setError(null);
        setSuccessMsg(null);

        try {
            // Fetch families and deployments in parallel
            const [filteredLinked, releasesList] = await Promise.all([
                fetchFamiliesData(),
                fetchReleasesData(),
                fetchGuiSchemas()
            ]);

            // Fetch deployments
            await fetchDeploymentsList();

            // Set default values for new deployment creation
            if (filteredLinked.length > 0) {
                // This will be handled by form state hook
            }

            if (releasesList.length > 0) {
                // This will be handled by form state hook
            }
        } catch (err) {
            console.error('Failed to load deployment page data', err);
            setError('Could not connect to deployment services.');
        } finally {
            setIsLoading(false);
        }
    }, [activeProjectId, fetchFamiliesData, fetchReleasesData, fetchGuiSchemas, fetchDeploymentsList]);

    // Reset data when project changes
    useEffect(() => {
        if (!activeProjectId) {
            setDeployments([]);
            setLinkedFamilies([]);
            setAllFamilies([]);
            setReleases([]);
            setGuiSchemas([]);
        }
    }, [activeProjectId]);

    return {
        deployments,
        linkedFamilies,
        allFamilies,
        releases,
        guiSchemas,
        isLoading,
        isSubmitting,
        error,
        successMsg,
        setError,
        setSuccessMsg,
        setIsSubmitting,
        fetchDeploymentsList,
        fetchFamiliesData,
        fetchReleasesData,
        fetchGuiSchemas,
        loadPageData
    };
}
